import type { Story } from "@repo/fika-shared/src/loader/story-schema.js"
import { format } from "date-fns"
import { createMemo, Show } from "solid-js"
import { useAppContext } from "@/context/app"
import type { SearchResult } from "@/data/search-index"
import { files } from "@/data/storage"
import { Box } from "@/designSystem/components/box"
import { Icon } from "@/designSystem/components/icon"
import { Text } from "@/designSystem/components/text"
import { extractDomain } from "@/lib/extractDomain"
import { formatDate } from "@/lib/formatDate"
import { getTitle } from "@/lib/getTitle"
import { generateSearchHighlighter } from "@/lib/searchHighlight"
import { Logo } from "../components/logo"
import { Mark } from "../components/mark"
import { type Pill, Pills } from "./pills"
import { Quote } from "./quote"
import {
  boxClass,
  publisherClass,
  storyClass,
  storyTitleClass,
  textClass,
  timeClass,
  topClass,
} from "./story.css"

type Props = {
  story: Story
  searchResult?: SearchResult
  namespace?: string
}

export function StoryComponent(props: Props) {
  const { db } = useAppContext()
  const bookmark = createMemo(() =>
    db.bookmark.findBy({ story_id: props.story.id }),
  )
  const title = createMemo(() => getTitle(props.story.dc_title))
  const hl = generateSearchHighlighter(props.searchResult)

  const titleHl = createMemo(() => hl("title", title()))
  const descriptionHl = createMemo(() =>
    hl("description", props.story.summary ?? props.story.dc_description),
  )
  const contentHl = createMemo(() => hl("body"))

  const storyDate = createMemo(
    () => bookmark()?.bookmarked_at || props.story.dc_created_at,
  )
  const humanReadableDate = createMemo(() => {
    const date = storyDate()
    if (!date) return null

    return formatDate(date)
  })
  const isoDate = createMemo(() => {
    const date = storyDate()
    if (!date) return undefined

    return format(date, "PPp")
  })
  const downloaded = createMemo(() => files().has(props.story.id))
  const publisher = createMemo(() => {
    const feedUrl = props.story.feed_url
    if (!feedUrl) return null

    return db.publisher.findBy({ url: feedUrl })
  })

  const getPost = createMemo(() => {
    const bookmarkId = bookmark()?.id
    if (!bookmarkId) return null

    return db.post.findBy({ bookmark_id: bookmarkId })
  })

  const getTags = createMemo(() => {
    const target = bookmark()
    if (!target) return []

    return db.tag
      .filterBy({ target_id: target.id, target_type: "bookmark" })
      .map(t => t.tag)
  })

  const pills = createMemo(() => {
    const pills: Pill[] = []

    if (downloaded()) {
      pills.push({
        icon: "download-cloud",
        label: "offline",
        hue: "emerald",
      })
    }

    if (getPost()) {
      pills.push({
        icon: "book-text",
        label: "blogmark",
        hue: "amber",
      })
    }

    return pills.concat(
      getTags().map(tag => {
        return {
          label: tag,
          rounded: true,
          hue: "sand",
        }
      }),
    )
  })

  return (
    <div class={storyClass}>
      <Box gap="24" flexDirection="row">
        <Box gap="16" fill class={boxClass}>
          <Box gap="8" fill>
            <div class={topClass}>
              <Text
                color="sand800"
                fontSize="s"
                fontWeight="500"
                class={publisherClass}
              >
                {publisher()?.dc_title || extractDomain(props.story.url)}
              </Text>
              {" · "}
              <time class={timeClass} title={isoDate()} datetime={isoDate()}>
                {humanReadableDate()}
              </time>
            </div>
            <Box fill class={textClass}>
              <span class={storyTitleClass}>
                <Mark hue="emerald" pieces={titleHl()} />
              </span>
              <Show when={descriptionHl()}>
                {description => (
                  <>
                    <Text color="sand400">{" — "}</Text>
                    <Mark hue="emerald" pieces={description()} />
                  </>
                )}
              </Show>
            </Box>
            <Quote pieces={contentHl()} />
          </Box>
        </Box>
        <Logo
          image={{ url: props.story.image_url, imageClass: "thumb" }}
          srcset={[
            {
              url: props.story.logo_url,
              imageClass: "logo",
              breakpoint: "xs",
            },
            {
              url: props.story.image_url,
              imageClass: "smallThumb",
              breakpoint: "s",
            },
          ]}
          fallback={<Icon name="book-marked" color="emerald700" />}
          alt={title()}
        />
      </Box>
      <Pills pills={pills()} />
    </div>
  )
}
